// ─────────────────────────────────────────────────────────────────────────────
// RunControlBar.tsx — THE RUN CONTROL, ONE COMPONENT FOR BUCK AND DELTA.
//
// Presentational. It renders a `RunSurface` (lib/runSurface.ts) and calls the two
// presses it is handed; it reads nothing and decides nothing. Buck's adapter is
// `HarnessRunControl`, Delta's is `DeltaRunControl`.
//
// STOP ENDS, PAUSE HOLDS — AND THEY LOOK IT. STOP carries a square, PAUSE two
// bars, and they sit apart with the gap between them. PAUSE and RESUME are
// DISABLED because no runner publishes a pause, and the gap is written on the
// bar itself rather than hidden in the menu. A surface with no pause (Delta's,
// from 2026-09-28) renders no hold group at all.
//
// HALTED KEEPS ITS DIAMOND. The mark is shape first, colour second: the one
// state that means something is broken reads at a glance against OFF's disc,
// buttons or no buttons.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import { MC } from '../missioncontrol/theme';
import { RunSurface, RunButton, RunMark, RunTone } from '../../lib/runSurface';

const TONE: Record<RunTone, string> = {
  danger: MC.red, go: MC.teal, wait: MC.amber, done: MC.green, muted: MC.muted, unread: MC.dim,
};

export function markStyle(mark: RunMark, colour: string): React.CSSProperties {
  const box: React.CSSProperties = { width: 9, height: 9, boxSizing: 'border-box', flexShrink: 0,
                                     display: 'inline-block' };
  if (mark === 'diamond') {
    return { ...box, background: colour, borderRadius: '1px',
             transform: 'rotate(45deg) scale(0.8)', boxShadow: `0 0 8px ${colour}` };
  }
  if (mark === 'disc') return { ...box, background: colour, borderRadius: '50%' };
  return { ...box, background: 'transparent', border: `1px solid ${colour}`, borderRadius: '50%' };
}

function Btn({ b, icon, onPress, testId, emphasis }: {
  b: RunButton; icon: string; onPress?: () => void; testId: string; emphasis?: string;
}) {
  const live = b.enabled && !!onPress;
  return (
    <button
      type="button"
      data-testid={testId}
      disabled={!live}
      title={b.why}
      onClick={live ? onPress : undefined}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        background: live && emphasis ? MC.tealSoft : 'transparent',
        border: `1px solid ${live ? (emphasis || MC.line) : MC.line2}`,
        color: live ? (emphasis ? MC.teal : MC.text) : MC.dim,
        borderRadius: 10, padding: '6px 12px', font: 'inherit', fontSize: 12,
        letterSpacing: '.06em', cursor: live ? 'pointer' : 'default',
        boxShadow: live ? `inset 0 1px 0 ${MC.topHighlight}` : 'none',
      }}
    >
      <span aria-hidden="true" style={{ fontSize: 10 }}>{icon}</span>
      {b.label}
    </button>
  );
}

export interface RunControlBarProps {
  surface: RunSurface;
  onRun?: () => void;
  onStop?: () => void;
  /** A transport fact about the request. Never moves the caption. */
  sending?: boolean;
  /** One short prompt the adapter owns (Delta: SELECT A COMPANY). */
  note?: string | null;
  testId?: string;
}

export default function RunControlBar(
  { surface: s, onRun, onStop, sending = false, note = null, testId = 'run-control-bar' }: RunControlBarProps,
) {
  const [open, setOpen] = React.useState(false);
  const colour = TONE[s.tone];
  const menuId = `${testId}-menu`;

  return (
    <div
      data-testid={testId}
      data-run-agent={s.agent}
      data-run-state={s.state}
      style={{
        display: 'flex', flexDirection: 'column', gap: 8,
        background: MC.panel2, borderRadius: 14, padding: '10px 14px',
        border: `1px solid ${MC.line2}`, boxShadow: MC.cardShadow,
      }}
    >
      {s.banner && (
        <span data-testid={`${testId}-banner`} data-banner-tone={s.banner.tone}
              style={{ color: TONE[s.banner.tone], fontSize: 12, letterSpacing: '.06em' }}>
          {s.banner.text}
        </span>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 150 }}>
          <span data-testid={`${testId}-mark`} data-run-mark={s.mark} style={markStyle(s.mark, colour)} />
          <span data-testid={`${testId}-caption`} style={{ color: colour, fontSize: 12, letterSpacing: '.06em' }}>
            {s.caption}
          </span>
        </span>

        <Btn b={s.run} icon="▶" onPress={onRun} testId={`${testId}-run`} emphasis={MC.tealEdge} />
        <Btn b={s.stop} icon="■" onPress={onStop} testId={`${testId}-stop`} />

        {/* THE HOLD GROUP, SET APART — pause is not stop. Only when the surface
            carries one: Delta's does not (owner ruling 2026-09-28 — "Resume not
            published either works or goes"). */}
        {s.pause && s.resume && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 8,
                         paddingLeft: 10, borderLeft: `1px solid ${MC.line2}` }}>
            <Btn b={s.pause} icon="‖" testId={`${testId}-pause`} />
            <Btn b={s.resume} icon="▶" testId={`${testId}-resume`} />
            <span data-testid={`${testId}-pause-gap`} style={{ color: MC.dim, fontSize: 10, letterSpacing: '.06em' }}>
              {s.pauseGap}
            </span>
          </span>
        )}

        <button
          type="button"
          data-testid={`${testId}-details`}
          aria-expanded={open}
          aria-controls={menuId}
          onClick={() => setOpen((o) => !o)}
          style={{
            background: 'transparent', border: `1px solid ${MC.line2}`, color: MC.muted,
            borderRadius: 10, padding: '6px 10px', font: 'inherit', fontSize: 11,
            letterSpacing: '.06em', cursor: 'pointer',
          }}
        >
          DETAILS <span aria-hidden="true">{open ? '▴' : '▾'}</span>
        </button>

        {sending && <span style={{ color: MC.dim, fontSize: 11 }}>SENDING</span>}
        {note && <span style={{ color: MC.dim, fontSize: 11 }}>{note}</span>}
      </div>

      {s.detail && (
        <span data-testid={`${testId}-detail`} style={{ color: MC.muted, fontSize: 12 }}>{s.detail}</span>
      )}

      {open && (
        <ul
          id={menuId}
          data-testid={menuId}
          style={{ listStyle: 'none', margin: 0, padding: '8px 10px', borderRadius: 10,
                   background: MC.panel3, border: `1px solid ${MC.line2}`,
                   boxShadow: `inset 0 1px 0 ${MC.topHighlight}`,
                   display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 14, rowGap: 4 }}
        >
          {s.menu.map((row) => (
            <li key={row.key} data-testid={`${menuId}-${row.key}`} data-gap={row.gap ? 'true' : 'false'}
                style={{ display: 'contents' }}>
              <span style={{ color: MC.dim, fontSize: 11, letterSpacing: '.06em' }}>{row.label}</span>
              <span style={{ color: row.gap ? MC.dim : MC.text, fontSize: 12,
                             fontStyle: row.gap ? 'italic' : 'normal' }}>
                {row.gap || row.value}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
